/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { Alert, Button as AntButton } from 'antd'
import { UserAddOutlined } from '@ant-design/icons'
import { useTeamPlayerFinderSettings } from '@sportninja/common/features/team/playerFinder/hooks/useTeamPlayerFinderSettings'
import { pickDefaultPlayerFinderRegistration } from '@sportninja/common/features/team/playerFinder/utils/defaultRegistration'
import { registrationsForRoster } from '@sportninja/common/features/team/playerFinder/utils/rosterScope'
import EmptyStateBox from 'src/components/ts-components/EmptyStateBox'
import {
  balanceLabel,
  balanceText,
} from 'src/features/team-balance/utils/balanceI18n'
import PlayerFinderRegistrationPicker from './PlayerFinderRegistrationPicker'
import RecruitingSettingsCard from './RecruitingSettingsCard'
import TeamPlayerFinderSkeleton from './TeamPlayerFinderSkeleton'

interface TeamPlayerFinderPanelProps {
  teamId: string
  /** Keys the per-caller settings cache shared with the Team page probe. */
  authUserId?: string | number | null
  /**
   * Open on this team registration — set when the rep followed a link from the
   * Team Budget sheet. Falls back to the default choice when it is not (or no
   * longer) one the caller can manage.
   */
  preferredRegistrationId?: string | null
  /**
   * The Registration tab's scope row, where the competition picker belongs
   * (beside the sub-tab switcher, like the Team Budget roster picker). Absent,
   * the picker renders in place above the card.
   */
  pickerSlot?: HTMLElement | null
  /** Switches to the Team Budget sub-tab; absent when there is none. */
  onOpenTeamBudget?: () => void
  /**
   * SN-8302 — scope the panel to ONE roster (opened from that roster on the Roster
   * tab): only this roster's registrations are offered, and when none is left the
   * panel says so instead of falling back to another roster's or competition's ad.
   * Absent, the panel is team-wide (the Registration tab).
   */
  rosterId?: string | null
}

/**
 * SN-4709 × SN-8081 — the Team page's Player Finder settings (Registration tab ›
 * Player Finder). Budget-independent: every team registration whose ad the
 * caller may manage is listed, whether or not the division uses a Team-Managed
 * Budget.
 *
 * The server owns every rule — who may manage the ad, whether recruiting can be
 * turned on, whether search shows the team — and this panel only renders its
 * answer and sends the rep's changes back.
 */
const TeamPlayerFinderPanel: React.FC<TeamPlayerFinderPanelProps> = ({
  teamId,
  authUserId = null,
  preferredRegistrationId = null,
  pickerSlot = null,
  onOpenTeamBudget,
  rosterId = null,
}) => {
  const {
    registrations: teamRegistrations,
    isLoading,
    isError,
    refetch,
  } = useTeamPlayerFinderSettings({ teamId, authUserId })
  const [selectedId, setSelectedId] = useState<string | null>(
    preferredRegistrationId
  )

  // Scoped BEFORE the selection and the picker, so neither can reach past the roster.
  const registrations = registrationsForRoster(teamRegistrations, rosterId)

  const selected = pickDefaultPlayerFinderRegistration(
    registrations,
    selectedId
  )

  if (isLoading) {
    return (
      <div css={containerStyle}>
        <TeamPlayerFinderSkeleton />
      </div>
    )
  }

  if (isError) {
    return (
      <div css={containerStyle}>
        <Alert
          type='error'
          showIcon
          data-testid='team-player-finder-error'
          message={balanceText('common:teamPlayerFinderLoadError')}
          action={
            <AntButton size='small' onClick={() => refetch()}>
              {balanceLabel('common:balanceRetry')}
            </AntButton>
          }
        />
      </div>
    )
  }

  if (selected === null) {
    return (
      <div css={containerStyle}>
        <EmptyStateBox
          icon={<UserAddOutlined />}
          title={balanceText('common:teamPlayerFinderEmptyTitle')}
          subtitle={balanceText(
            rosterId
              ? 'common:teamPlayerFinderSeasonEmptyBody'
              : 'common:teamPlayerFinderEmptyBody'
          )}
        />
      </div>
    )
  }

  const picker = (
    <PlayerFinderRegistrationPicker
      registrations={registrations}
      selectedId={selected.id}
      onSelect={setSelectedId}
    />
  )

  return (
    <div css={containerStyle}>
      {/*
        On the Registration tab's scope row (the slot) the Season select already
        names the season, so a single ad needs no picker there — it would only
        repeat it. Two or more (one season, several divisions) still need one.
        In place (the Roster tab) the single-entry text stays: nothing else on
        that surface names the competition.
      */}
      {pickerSlot ? (
        registrations.length > 1 ? (
          createPortal(picker, pickerSlot)
        ) : null
      ) : (
        <div css={inlinePickerStyle}>{picker}</div>
      )}
      <RecruitingSettingsCard
        key={selected.id}
        entry={selected}
        teamId={teamId}
        authUserId={authUserId}
        onOpenTeamBudget={onOpenTeamBudget}
      />
    </div>
  )
}

export default TeamPlayerFinderPanel

// ---- styled ----
const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
`

const inlinePickerStyle = css`
  display: flex;
  align-items: center;
`
